import React, { useState, useRef, useEffect } from "react";
import { GlobeAltIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../context/LanguageContext";
import { supportedLanguages } from "../utils/languageUtils";
import { cn } from "../utils/cn";

interface LanguageSelectorProps {
  className?: string;
  showLabel?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = "",
  showLabel = true,
}) => {
  const { currentLanguage, setLanguage, isTranslating } = useLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = (languageCode: string) => {
    setLanguage(languageCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const currentLanguageConfig = supportedLanguages.find(
    (lang) => lang.code === currentLanguage
  );

  // Always show the language selector - Google Translate will load on demand

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      {showLabel && (
        <>
          <GlobeAltIcon className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Language:</span>
        </>
      )}

      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isTranslating}
          className={cn(
            "flex items-center space-x-2 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700",
            "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "hover:border-gray-400 transition-colors",
            isTranslating && "animate-pulse"
          )}
          aria-label="Select Language"
        >
          {currentLanguageConfig && (
            <>
              <img
                src={currentLanguageConfig.flag}
                alt={`${currentLanguageConfig.name} flag`}
                className="w-4 h-3 object-cover rounded-sm"
                onError={(e) => {
                  // Fallback to emoji if image fails to load
                  e.currentTarget.style.display = "none";
                  const nextElement = e.currentTarget
                    .nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.style.display = "inline";
                  }
                }}
              />
              <span className="hidden">
                {currentLanguageConfig.code === "en" ? "🇬🇧" : "🇱🇰"}
              </span>
              <span>{currentLanguageConfig.nativeName}</span>
            </>
          )}
          <ChevronDownIcon className="w-4 h-4 text-gray-400" />
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-50">
            {supportedLanguages.map((language) => (
              <button
                key={language.code}
                type="button"
                onClick={() => handleLanguageChange(language.code)}
                className={cn(
                  "w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors",
                  currentLanguage === language.code &&
                    "bg-primary-50 text-primary-700"
                )}
              >
                <img
                  src={language.flag}
                  alt={`${language.name} flag`}
                  className="w-4 h-3 object-cover rounded-sm"
                  onError={(e) => {
                    // Fallback to emoji if image fails to load
                    e.currentTarget.style.display = "none";
                    const nextElement = e.currentTarget
                      .nextElementSibling as HTMLElement;
                    if (nextElement) {
                      nextElement.style.display = "inline";
                    }
                  }}
                />
                <span className="hidden">
                  {language.code === "en" ? "🇬🇧" : "🇱🇰"}
                </span>
                <span>{language.nativeName}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {isTranslating && (
        <div className="flex items-center space-x-1 text-sm text-primary-600">
          <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          <span>Translating...</span>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
